import { Autocomplete, Chip, TextField } from "@mui/material";
import { CrimeCodeModel } from "@rubi/api-generated";

/** "Title (RMS-CODE)" label, used for both options and selected chips. */
export const crimeCodeLabel = (option: CrimeCodeModel) => {
  const codeDisplay = option.rms_code_extension
    ? `${option.rms_code}-${option.rms_code_extension}`
    : option.rms_code;
  return `${option.title} (${codeDisplay})`;
};

// Match the condensed-report tagbox pill (condensed-report.css .sv-tagbox__item):
// a compact gray chip — action.selected fill, text.primary label, full-pill
// radius, and a dark delete icon.
const tagboxChipSx = {
  height: "1.25rem",
  borderRadius: "var(--cr-radius-pill, 0.8125rem)",
  backgroundColor: "action.selected",
  color: "text.primary",
  fontSize: "0.8125rem",
  fontWeight: 400,
  "& .MuiChip-label": { paddingInline: "0.5rem" },
  "& .MuiChip-deleteIcon": {
    fontSize: "0.875rem",
    color: "action.disabled",
    "&:hover": { color: "action.active" },
  },
};

export interface CrimeCodesMultiSelectProps {
  options: CrimeCodeModel[];
  value: CrimeCodeModel[];
  onChange: (event: React.SyntheticEvent, value: CrimeCodeModel[]) => void;
  loading?: boolean;
  readOnly?: boolean;
  required?: boolean;
}

/**
 * Presentational crime-codes multi-select: a MUI Autocomplete whose selected
 * chips match the condensed-report tagbox pill. Holds no data fetching — the
 * caller (CrimeCodeSelector) owns options, value, and persistence.
 */
const CrimeCodesMultiSelect = ({
  options,
  value,
  onChange,
  loading = false,
  readOnly = false,
  required = false,
}: CrimeCodesMultiSelectProps) => (
  <Autocomplete
    multiple
    disabled={readOnly}
    options={options}
    value={value}
    onChange={onChange}
    getOptionLabel={crimeCodeLabel}
    isOptionEqualToValue={(option, selected) => option.id === selected.id}
    loading={loading}
    renderInput={(params) => (
      <TextField
        {...params}
        variant="outlined"
        label="Crime Codes"
        placeholder="Select crime codes"
        required={required}
        slotProps={{
          inputLabel: {
            sx: {
              "& .MuiFormLabel-asterisk": { color: "error.main" },
            },
          },
        }}
      />
    )}
    renderValue={(selected, getTagProps) =>
      selected.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={option.id}
          size="small"
          label={crimeCodeLabel(option)}
          sx={tagboxChipSx}
        />
      ))
    }
  />
);

export default CrimeCodesMultiSelect;
